import { useState, FC, useCallback } from 'react';
import { Input, Icon } from "@components";
import { ColorsEnum } from '@utilities/enums';

interface FilterOption {
    value: string;
    label: string;
}

interface GenericActionsBarProps {
    searchPlaceholder?: string;
    selectedFilterValue?: string;
    filterOptions?: FilterOption[];
    customActions?: React.ReactNode[];
    handleFilterChange?: (value: string) => void;
    handleSearch?: (value: string) => void;
}

export const GenericActionsBar: FC<GenericActionsBarProps> = ({
    searchPlaceholder = 'Rechercher...',
    selectedFilterValue,
    filterOptions = [],
    customActions = [],
    handleFilterChange,
    handleSearch,
}) => {

    const [showFilter, setShowFilter] = useState<boolean>(false);
    const [showSearch, setShowSearch] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');

    const toggleShowFilter = useCallback(() => setShowFilter((prev) => !prev), []);
    const toggleShowSearch = useCallback(()=> {
        setShowSearch((prev) => !prev);
        if (showSearch) {
            setSearchValue('');
            handleSearch && handleSearch('');
        }
    }, [showSearch, handleSearch]);

    const onClearFilter = useCallback(() => {
        handleFilterChange && handleFilterChange('');
        setShowFilter(false);
    }, [handleFilterChange]);

    const renderSearchInput = useCallback(() => (
        <div className="flex items-center gap-2 w-full animate-slide-in">
            <Input
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => {
                    handleSearch && handleSearch(e.target.value);
                    setSearchValue(e.target.value);
                }}
                className="flex-grow h-10"
            />
            <button
                onClick={toggleShowSearch}
                className="btn btn-circle btn-ghost"
            >
                <Icon iconName="X" size={20} />
            </button>
        </div>
    ), [searchPlaceholder, searchValue, handleSearch, toggleShowSearch]);

    const renderFilterDropdown = useCallback(() => (
        <div className="absolute top-full right-0 mt-2 z-10">
            <ul className="w-52 bg-base-100 rounded-box shadow-lg border border-gray-200">
                {
                    filterOptions.map((option) => (
                        <li
                            key={option.value}
                            className={`rounded-lg cursor-pointer p-2 ${
                                selectedFilterValue === option.value ? 'bg-primary text-primary-content' : 'hover:bg-gray-100'
                            }`}
                            onClick={() => {
                                handleFilterChange && handleFilterChange(option.value);
                            }}
                        >
                            {option.label}
                        </li>
                    ))
                }
            </ul>
        </div>
    ), [filterOptions, selectedFilterValue, handleFilterChange]);

    return (
        <div className="flex items-center gap-4 relative">
        {
            showSearch ? renderSearchInput() 
            : (
                <div className="flex items-center gap-4">
                    {(!selectedFilterValue && handleSearch) && (
                        <button
                            className="btn btn-ghost btn-circle"
                            onClick={toggleShowSearch}
                        >
                            <Icon iconName="Search" size={20} />
                        </button>
                    )}
                    {filterOptions.length > 0 && (
                        <div className="relative">
                            {
                                selectedFilterValue && (
                                    <>
                                        <span className="badge badge-primary badge-outline">
                                            {filterOptions.find(option => option.value === selectedFilterValue)?.label}
                                        </span>
                                        <button
                                            className="btn btn-ghost btn-circle"
                                            onClick={onClearFilter}
                                        >
                                            <Icon iconName="FilterX" size={20} color={ColorsEnum.ERROR} />
                                        </button>
                                    </>
                                )
                            }
                            <button
                                className="btn btn-ghost btn-circle"
                                onClick={toggleShowFilter}
                            >
                                <Icon iconName="Filter" size={20} />
                            </button>

                            {showFilter && renderFilterDropdown()}
                        </div>
                    )}

                    {/* Custom action buttons */}
                    {
                        !selectedFilterValue && 
                        customActions.map((action, index) => (
                            <div key={index}>{action}</div>
                        ))
                    }
                </div>
            )
        }
        </div>
    );
};
